import React from 'react';
import './ETL_Tool.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SchemaVisualizer from '../components/SchemaVisualizer';
import api from '../services/api';
// Import script generator components
import ScriptGeneratorHeader from '../components/ScriptGeneratorHeader';
import ScriptGeneratorControlPanel from '../components/ScriptGeneratorControlPanel';
import ScriptEditor from '../components/ScriptEditor';
import ScriptActions from '../components/ScriptActions';
import BatchInfo from '../components/BatchInfo';
import ScriptHistorySidebar from '../components/ScriptHistorySidebar';
import UserDataModelsDropdown from '../components/UserDataModelsDropdown';

function ETL_Tool() {
  const [sourceJson, setSourceJson] = useState('');
  const [targetJson, setTargetJson] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sourceJsonError, setSourceJsonError] = useState('');
  const [targetJsonError, setTargetJsonError] = useState('');
  const [sourceFileName, setSourceFileName] = useState('No file chosen');
  const [targetFileName, setTargetFileName] = useState('No file chosen');
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [parsedSourceSchema, setParsedSourceSchema] = useState(null);
  const [parsedTargetSchema, setParsedTargetSchema] = useState(null);
  
  // New state variables for ETL mapping
  const [mappingResults, setMappingResults] = useState(null);
  const [isMappingLoading, setIsMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState('');
  const [showMappingResults, setShowMappingResults] = useState(false);

  // New state variables for user data models
  const [selectedUserModel, setSelectedUserModel] = useState(null);

  const [isGeneratingScripts, setIsGeneratingScripts] = useState(false);
  const [transformationScripts, setTransformationScripts] = useState(null);
  const [scriptGenerationError, setScriptGenerationError] = useState('');
  const navigate = useNavigate();

  // Add script generator state variables
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [generatedScripts, setGeneratedScripts] = useState({});
  const [finalScript, setFinalScript] = useState('');
  const [scriptLanguage, setScriptLanguage] = useState('javascript');
  const [editorTheme, setEditorTheme] = useState('vs-dark');
  const [scriptHistory, setScriptHistory] = useState([]);
  const [currentScriptId, setCurrentScriptId] = useState(null);
  const [scriptMetadata, setScriptMetadata] = useState({});
  const [selectedTechnology, setSelectedTechnology] = useState('informatica');
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);

  // Helper function to calculate schema statistics
  const calculateSchemaStats = (schema) => {
    if (!schema || !schema.tables) return { tableCount: 0, columnCount: 0, tableNames: [] };
    
    const tableCount = schema.tables.length;
    const columnCount = schema.tables.reduce((total, table) => 
      total + (table.attributes ? table.attributes.length : 0), 0);
    const tableNames = schema.tables.map(table => table.name);
    
    return { tableCount, columnCount, tableNames };
  };

  // Helper function to calculate mapping statistics
  const calculateMappingStats = (mappings) => {
    if (!mappings || !Array.isArray(mappings)) {
      return { 
        totalMappings: 0, 
        mappedColumns: 0, 
        unmappedColumns: 0, 
        avgConfidence: 0,
        highConfidenceMappings: 0,
        mediumConfidenceMappings: 0,
        lowConfidenceMappings: 0,
        mappingSuccessRate: 0
      };
    }
    
    const totalMappings = mappings.length;
    
    // Filter out invalid or empty mappings
    const validMappings = mappings.filter(m => m && m.source_column);
    
    // Count mapped vs unmapped columns
    const mappedColumns = validMappings.filter(m => 
      m.target_column && 
      m.target_column !== 'No mapping' && 
      m.target_column !== '' && 
      m.target_column !== null
    ).length;
    
    const unmappedColumns = validMappings.length - mappedColumns;
    
    // Calculate confidence statistics
    const validConfidenceMappings = validMappings.filter(m => 
      m.confidence !== undefined && 
      m.confidence !== null && 
      !isNaN(m.confidence)
    );
    
    const avgConfidence = validConfidenceMappings.length > 0 ? 
      Math.round(validConfidenceMappings.reduce((sum, m) => sum + (m.confidence || 0), 0) / validConfidenceMappings.length) : 0;
    
    // Count mappings by confidence level
    const highConfidenceMappings = validConfidenceMappings.filter(m => m.confidence >= 90).length;
    const mediumConfidenceMappings = validConfidenceMappings.filter(m => m.confidence >= 70 && m.confidence < 90).length;
    const lowConfidenceMappings = validConfidenceMappings.filter(m => m.confidence < 70).length;
    
    // Calculate mapping success rate
    const mappingSuccessRate = validMappings.length > 0 ? 
      Math.round((mappedColumns / validMappings.length) * 100) : 0;
    
    return { 
      totalMappings: validMappings.length, 
      mappedColumns, 
      unmappedColumns, 
      avgConfidence,
      highConfidenceMappings,
      mediumConfidenceMappings,
      lowConfidenceMappings,
      mappingSuccessRate
    };
  };

  // Helper function to extract table name from column name
  const extractTableName = (columnName) => {
    if (!columnName) return '';
    // Check if column name contains table prefix (e.g., "table_name.column_name")
    const parts = columnName.split('.');
    if (parts.length > 1) {
      return parts[0];
    }
    return '';
  };

  // Helper function to get table name from schema by column name
  const getTableNameFromSchema = (columnName, schema) => {
    if (!schema || !schema.tables || !columnName) return '';
    
    for (const table of schema.tables) {
      if (table.attributes && table.attributes.some(attr => attr.name === columnName)) {
        return table.name;
      }
    }
    return '';
  };

  // JSON validation function
  const validateSchemaFormat = (schema, schemaName) => {
    if (!schema || typeof schema !== 'object') {
      return `${schemaName}: Invalid JSON structure`;
    }

    if (!schema.name || typeof schema.name !== 'string') {
      return `${schemaName}: Missing or invalid 'name' field`;
    }

    if (!schema.tables || !Array.isArray(schema.tables)) {
      return `${schemaName}: Missing or invalid 'tables' array`;
    }

    if (schema.tables.length === 0) {
      return `${schemaName}: Tables array cannot be empty`;
    }

    for (let i = 0; i < schema.tables.length; i++) {
      const table = schema.tables[i];
      
      if (!table.name || typeof table.name !== 'string') {
        return `${schemaName}: Table ${i + 1} missing or invalid 'name' field`;
      }

      if (!table.attributes || !Array.isArray(table.attributes)) {
        return `${schemaName}: Table '${table.name}' missing or invalid 'attributes' array`;
      }

      if (table.attributes.length === 0) {
        return `${schemaName}: Table '${table.name}' attributes array cannot be empty`;
      }

      for (let j = 0; j < table.attributes.length; j++) {
        const attribute = table.attributes[j];
        
        if (!attribute.name || typeof attribute.name !== 'string') {
          return `${schemaName}: Table '${table.name}' attribute ${j + 1} missing or invalid 'name' field`;
        }

        if (!attribute.data_type || typeof attribute.data_type !== 'string') {
          return `${schemaName}: Table '${table.name}' attribute '${attribute.name}' missing or invalid 'data_type' field`;
        }

        // Optional fields validation
        if (attribute.description !== undefined && typeof attribute.description !== 'string') {
          return `${schemaName}: Table '${table.name}' attribute '${attribute.name}' description must be a string`;
        }

        if (attribute.is_primary_key !== undefined && typeof attribute.is_primary_key !== 'boolean') {
          return `${schemaName}: Table '${table.name}' attribute '${attribute.name}' is_primary_key must be a boolean`;
        }

        if (attribute.is_foreign_key !== undefined && typeof attribute.is_foreign_key !== 'boolean') {
          return `${schemaName}: Table '${table.name}' attribute '${attribute.name}' is_foreign_key must be a boolean`;
        }

        if (attribute.references !== undefined && attribute.references !== null && typeof attribute.references !== 'object') {
          return `${schemaName}: Table '${table.name}' attribute '${attribute.name}' references must be an object or null`;
        }
      }
    }

    // Validate samples if present
    if (schema.samples !== undefined) {
      if (typeof schema.samples !== 'object' || schema.samples === null) {
        return `${schemaName}: 'samples' field must be an object`;
      }
      
      for (const [tableName, sampleData] of Object.entries(schema.samples)) {
        if (!Array.isArray(sampleData)) {
          return `${schemaName}: Sample data for table '${tableName}' must be an array`;
        }
        
        // Check if table exists in schema
        const tableExists = schema.tables.some(table => table.name === tableName);
        if (!tableExists) {
          return `${schemaName}: Sample data references non-existent table '${tableName}'`;
        }
      }
    }

    return null; // No errors
  };

  // Helper to unwrap schema if wrapped in a single key
  function unwrapSchema(obj) {
    if (
      obj &&
      typeof obj === 'object' &&
      !Array.isArray(obj) &&
      Object.keys(obj).length === 1
    ) {
      const inner = obj[Object.keys(obj)[0]];
      if (inner && typeof inner === 'object' && inner.tables) {
        return inner;
      }
    }
    return obj;
  }

  const handleETL = async () => {
    setError('');
    setSuccess('');
    setMappingError('');
    setMappingResults(null);
    setShowMappingResults(false);
    
    try {
      let source = JSON.parse(sourceJson);
      let target = JSON.parse(targetJson);
      source = unwrapSchema(source);
      target = unwrapSchema(target);
      
      // Validate schema formats
      const sourceValidationError = validateSchemaFormat(source, 'Source Schema');
      const targetValidationError = validateSchemaFormat(target, 'Target Schema');
      
      if (sourceValidationError) {
        setError(sourceValidationError);
        return;
      }
      
      if (targetValidationError) {
        setError(targetValidationError);
        return;
      }
      
      // Store parsed schemas for visualization
      setParsedSourceSchema(source);
      setParsedTargetSchema(target);
      setSuccess('Both JSON inputs are valid. Ready for ETL process.');
      
      // Generate ETL mapping
      await generateETLMapping(source, target);
      
    } catch (e) {
      setError('Invalid JSON input. Please check your source and target JSON.');
    }
  };

  const generateETLMapping = async (sourceSchema, targetSchema) => {
    setIsMappingLoading(true);
    setMappingError('');
    
    try {
      console.log('Generating ETL mapping with schemas:', { sourceSchema, targetSchema });
      
      const response = await api.generateETLMapping(sourceSchema, targetSchema);
      
      console.log('ETL mapping response:', response.data);
      setMappingResults(response.data);
      setShowMappingResults(true);
      setSuccess('ETL mapping generated successfully!');
      
    } catch (error) {
      console.error('Error generating ETL mapping:', error);
      setMappingError(error.response?.data?.detail || error.message || 'Failed to generate ETL mapping');
    } finally {
      setIsMappingLoading(false);
    }
  };

  const generateTransformationScripts = async () => {
    if (!mappingResults || mappingResults.length === 0) {
      setScriptGenerationError('No mapping results available. Please generate ETL mapping first.');
      return;
    }

    // Navigate to ScriptGeneratorPage with the required data
    navigate('/script-generator', {
      state: {
        mappingResults,
        sourceSchema: parsedSourceSchema,
        targetSchema: parsedTargetSchema
      }
    });
  };

  // Add script generation functions
  const getLanguageForTechnology = (technology) => {
    const tech = technology?.toLowerCase();
    if (tech === 'informatica') return 'sql';
    if (tech === 'pyspark') return 'python';
    return 'javascript';
  };

  const createBatches = (mappings, batchSize = 10) => {
    const batches = [];
    for (let i = 0; i < mappings.length; i += batchSize) {
      batches.push(mappings.slice(i, i + batchSize));
    }
    return batches;
  };

  const parseApiResponse = (response) => {
    const scripts = response.data.scripts || {};
    const metadata = response.data.metadata || {};
    const expectedFields = selectedTechnology.toLowerCase() === 'informatica'
      ? ['source_qualifier', 'expression_transformations', 'lookup_transformations', 'target_load_order', 'session_properties']
      : ['imports', 'dataframe_read', 'transformations', 'data_quality_checks', 'write_operations', 'error_handling'];
    const validatedScripts = {};
    expectedFields.forEach(field => {
      validatedScripts[field] = scripts[field] || '';
    });
    return { scripts: validatedScripts, metadata };
  };

  const generateBatchScripts = async (batchMappings, batchIndex) => {
    try {
      const response = await api.generateTransformationScripts({
        mapping_results: batchMappings,
        source_schema: parsedSourceSchema,
        target_schema: parsedTargetSchema,
        technology: selectedTechnology
      });
      const parsedResponse = parseApiResponse(response);
      return { batchIndex, scripts: parsedResponse.scripts, metadata: parsedResponse.metadata };
    } catch (error) {
      throw new Error(`Batch ${batchIndex + 1} failed: ${error.message}`);
    }
  };

  const aggregateScripts = (batchResults) => {
    const aggregated = {
      source_qualifier: '',
      expression_transformations: '',
      lookup_transformations: '',
      target_load_order: '',
      session_properties: '',
      imports: '',
      dataframe_read: '',
      transformations: '',
      data_quality_checks: '',
      write_operations: '',
      error_handling: ''
    };
    batchResults.forEach(({ scripts }) => {
      Object.keys(scripts).forEach(key => {
        if (scripts[key] && typeof scripts[key] === 'string') {
          if (aggregated[key]) {
            aggregated[key] += '\n\n' + scripts[key];
          } else {
            aggregated[key] = scripts[key];
          }
        }
      });
    });
    return aggregated;
  };

  const getScriptStatistics = (aggregatedScripts) => {
    const stats = { totalLines: 0, nonEmptySections: 0, totalSections: 0 };
    Object.keys(aggregatedScripts).forEach(key => {
      if (aggregatedScripts[key] && typeof aggregatedScripts[key] === 'string') {
        const lines = aggregatedScripts[key].split('\n').length;
        stats.totalLines += lines;
        if (aggregatedScripts[key].trim()) {
          stats.nonEmptySections++;
        }
        stats.totalSections++;
      }
    });
    return stats;
  };

  const compileFinalScript = (aggregatedScripts) => {
    if (selectedTechnology.toLowerCase() === 'informatica') {
      const informaticaScripts = {
        source_qualifier: aggregatedScripts.source_qualifier,
        expression_transformations: aggregatedScripts.expression_transformations,
        lookup_transformations: aggregatedScripts.lookup_transformations,
        target_load_order: aggregatedScripts.target_load_order,
        session_properties: aggregatedScripts.session_properties
      };
      return `-- Informatica Transformation Scripts\n-- Generated by Data Engineering Copilot\n-- Technology: ${selectedTechnology}\n-- Total Mappings: ${mappingResults?.length || 0}\n-- Generated on: ${new Date().toISOString()}\n\n-- ============================================\n-- SOURCE QUALIFIER TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.source_qualifier || '-- No source qualifier transformations'}\n\n-- ============================================\n-- EXPRESSION TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.expression_transformations || '-- No expression transformations'}\n\n-- ============================================\n-- LOOKUP TRANSFORMATIONS\n-- ============================================\n${informaticaScripts.lookup_transformations || '-- No lookup transformations'}\n\n-- ============================================\n-- TARGET LOAD ORDER\n-- ============================================\n${informaticaScripts.target_load_order || '-- No target load order specified'}\n\n-- ============================================\n-- SESSION PROPERTIES\n-- ============================================\n${informaticaScripts.session_properties || '-- No session properties specified'}`;
    } else if (selectedTechnology.toLowerCase() === 'pyspark') {
      const pysparkScripts = {
        imports: aggregatedScripts.imports,
        dataframe_read: aggregatedScripts.dataframe_read,
        transformations: aggregatedScripts.transformations,
        data_quality_checks: aggregatedScripts.data_quality_checks,
        write_operations: aggregatedScripts.write_operations,
        error_handling: aggregatedScripts.error_handling
      };
      return `# PySpark Transformation Scripts\n# Generated by Data Engineering Copilot\n# Technology: ${selectedTechnology}\n# Total Mappings: ${mappingResults?.length || 0}\n# Generated on: ${new Date().toISOString()}\n\n# ============================================\n# IMPORTS\n# ============================================\n${pysparkScripts.imports || '# Standard PySpark imports'}\n\n# ============================================\n# DATAFRAME READ OPERATIONS\n# ============================================\n${pysparkScripts.dataframe_read || '# No dataframe read operations'}\n\n# ============================================\n# TRANSFORMATION LOGIC\n# ============================================\n${pysparkScripts.transformations || '# No transformations specified'}\n\n# ============================================\n# DATA QUALITY CHECKS\n# ============================================\n${pysparkScripts.data_quality_checks || '# No data quality checks'}\n\n# ============================================\n# WRITE OPERATIONS\n# ============================================\n${pysparkScripts.write_operations || '# No write operations'}\n\n# ============================================\n# ERROR HANDLING AND LOGGING\n# ============================================\n${pysparkScripts.error_handling || '# No error handling specified'}`;
    }
    return JSON.stringify(aggregatedScripts, null, 2);
  };

  const generateScriptsWithBatching = async () => {
    if (!mappingResults || mappingResults.length === 0) {
      setScriptGenerationError('No mapping results available');
      return;
    }
    setIsGenerating(true);
    setGeneratedScripts({});
    setFinalScript('');
    try {
      const batches = createBatches(mappingResults, 10);
      setTotalBatches(batches.length);
      setCurrentBatch(0);
      const batchResults = [];
      for (let i = 0; i < batches.length; i++) {
        setCurrentBatch(i + 1);
        const batchResult = await generateBatchScripts(batches[i], i);
        batchResults.push(batchResult);
        setGeneratedScripts(prev => ({ ...prev, [`batch_${i}`]: batchResult.scripts }));
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      const aggregatedScripts = aggregateScripts(batchResults);
      const scriptStats = getScriptStatistics(aggregatedScripts);
      const finalCompiledScript = compileFinalScript(aggregatedScripts);
      setFinalScript(finalCompiledScript);
      const scriptMetadata = {
        name: `${selectedTechnology} Transformation Scripts`,
        totalLines: scriptStats.totalLines,
        nonEmptySections: scriptStats.nonEmptySections,
        totalSections: scriptStats.totalSections,
        batchesProcessed: batches.length,
        scriptStats: scriptStats
      };
      saveScriptToHistory(finalCompiledScript, scriptMetadata);
      setScriptLanguage(getLanguageForTechnology(selectedTechnology));
    } catch (error) {
      setScriptGenerationError(`Script generation failed: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setCurrentBatch(0);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(finalScript);
      setSuccess('Script copied to clipboard!');
    } catch (error) {
      setScriptGenerationError('Failed to copy to clipboard');
    }
  };

  const downloadScript = () => {
    const blob = new Blob([finalScript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTechnology.toLowerCase()}_transformation_scripts.${scriptLanguage === 'sql' ? 'sql' : 'py'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess('Script downloaded successfully!');
  };

  const saveScriptToHistory = (script, metadata) => {
    const scriptId = `script_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const scriptEntry = {
      id: scriptId,
      script: script,
      metadata: {
        ...metadata,
        technology: selectedTechnology,
        totalMappings: mappingResults?.length || 0,
        generatedAt: new Date().toISOString(),
        sourceTables: parsedSourceSchema?.tables?.length || 0,
        targetTables: parsedTargetSchema?.tables?.length || 0
      },
      createdAt: new Date().toISOString()
    };
    setScriptHistory(prev => [...prev, scriptEntry]);
    setCurrentScriptId(scriptId);
    setScriptMetadata(scriptEntry.metadata);
    return scriptId;
  };

  const loadScriptFromHistory = (scriptId) => {
    const scriptEntry = scriptHistory.find(entry => entry.id === scriptId);
    if (scriptEntry) {
      setFinalScript(scriptEntry.script);
      setCurrentScriptId(scriptId);
      setScriptMetadata(scriptEntry.metadata);
      setScriptLanguage(getLanguageForTechnology(scriptEntry.metadata.technology));
      setSuccess(`Loaded script: ${scriptEntry.metadata.name || 'Untitled'}`);
    }
  };

  const deleteScriptFromHistory = (scriptId) => {
    setScriptHistory(prev => prev.filter(entry => entry.id !== scriptId));
    if (currentScriptId === scriptId) {
      setFinalScript('');
      setCurrentScriptId(null);
      setScriptMetadata({});
    }
    setSuccess('Script deleted from history');
  };

  // Function to fetch user data models


  // Function to handle user model selection
  const handleUserModelSelection = (model) => {
    try {
      console.log('Selected model:', model);
      
      setSelectedUserModel(model);
      setError(''); // Clear any existing errors
      
      // Extract the schema from the data model
      const schema = model.etl_schema;
      console.log('Extracted schema:', schema);
      
      if (!schema) {
        throw new Error('No schema found in the selected data model');
      }
      
      if (!schema.tables || !Array.isArray(schema.tables)) {
        throw new Error('Invalid schema format: missing or invalid tables array');
      }
      
      const formattedJson = JSON.stringify(schema, null, 2);
      
      setTargetJson(formattedJson);
      const modelName = model.display_name || model.model_name || `Data Model ${model.id}`;
      setTargetFileName(modelName);
      setTargetJsonError('');
      setParsedTargetSchema(schema);
      setSuccess(`Selected data model: ${modelName}`);
      
      // Clear any file-uploaded schema
      const fileInput = document.getElementById('file_input_target');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      console.error('Error processing selected model:', err);
      setError(err.message);
      setTargetJsonError(err.message);
      setSelectedUserModel(null);
      setParsedTargetSchema(null);
    }
  };

  // Function to clear selected user model
  const clearSelectedUserModel = () => {
    setSelectedUserModel(null);
    setTargetJson('');
    setTargetFileName('No file chosen');
    setTargetJsonError('');
    setParsedTargetSchema(null);
  };

  const handleFileUpload = (file, isSource) => {
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const text = evt.target.result;
        let parsed = JSON.parse(text);
        parsed = unwrapSchema(parsed);
        if (isSource) {
          setSourceJson(text);
          setSourceJsonError('');
          setParsedSourceSchema(parsed);
        } else {
          setTargetJson(text);
          setTargetJsonError('');
          setParsedTargetSchema(parsed);
        }
      } catch {
        if (isSource) {
          setSourceJsonError('Uploaded file is not valid JSON');
        } else {
          setTargetJsonError('Uploaded file is not valid JSON');
        }
      }
    };
    reader.readAsText(file);
  };

  const toggleVisualizer = () => {
    if (parsedSourceSchema && parsedTargetSchema) {
      console.log('Opening visualizer with schemas:', {
        source: parsedSourceSchema,
        target: parsedTargetSchema
      });
      setShowVisualizer(!showVisualizer);
    } else {
      setError('Please upload both source and target JSON files first.');
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("etlToolState");
    if (saved) {
      const state = JSON.parse(saved);
      if (state.sourceJson) setSourceJson(state.sourceJson);
      if (state.targetJson) setTargetJson(state.targetJson);
      if (state.parsedSourceSchema) setParsedSourceSchema(state.parsedSourceSchema);
      if (state.parsedTargetSchema) setParsedTargetSchema(state.parsedTargetSchema);
      if (state.mappingResults) setMappingResults(state.mappingResults);
      if (state.showMappingResults) setShowMappingResults(state.showMappingResults);
      if (state.sourceFileName) setSourceFileName(state.sourceFileName);
      if (state.targetFileName) setTargetFileName(state.targetFileName);
      if (state.success) setSuccess(state.success);
      if (state.error) setError(state.error);
      if (state.mappingError) setMappingError(state.mappingError);
      if (state.selectedUserModel) setSelectedUserModel(state.selectedUserModel);
    }
  }, []);

  useEffect(() => {
    const stateToStore = {
      sourceJson,
      targetJson,
      parsedSourceSchema,
      parsedTargetSchema,
      mappingResults,
      showMappingResults,
      sourceFileName,
      targetFileName,
      success,
      error,
      mappingError,
      selectedUserModel,
    };
    localStorage.setItem("etlToolState", JSON.stringify(stateToStore));
  }, [
    sourceJson,
    targetJson,
    parsedSourceSchema,
    parsedTargetSchema,
    mappingResults,
    showMappingResults,
    sourceFileName,
    targetFileName,
    success,
    error,
    mappingError,
    selectedUserModel,
  ]);

  useEffect(() => {
    if (scriptHistory.length > 0) {
      sessionStorage.setItem('scriptHistory', JSON.stringify(scriptHistory));
    }
  }, [scriptHistory]);

  useEffect(() => {
    const stored = sessionStorage.getItem('scriptHistory');
    if (stored) {
      setScriptHistory(JSON.parse(stored));
    }
  }, []);



  // Add tab state
  const [activeTab, setActiveTab] = useState(0);

  // Tab headers
  const tabHeaders = [
    'Source & Target Schema',
    'ETL Mapping Results',
    'Script Generator',
  ];

  // Tab bodies
  const tabBodies = [
    // Tab 1: Source & Target Schema
    (
      <div className="bg-white rounded-lg shadow-sm border border-borderLight p-6">
        <div className="space-y-6 max-w-2xl mx-auto">
          {/* Instructions */}
          <div className="text-sm text-textSecondary mb-4 p-3 bg-[rgba(8,145,178,0.08)] rounded-lg border border-[rgba(8,145,178,0.3)] max-w-lg mx-auto">
            <p className="font-semibold mb-2">📋 Instructions:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Upload your source database schema in JSON or Excel file</li>
              <li>Upload your target database schema in JSON or Excel file <strong>OR</strong> select from your generated data models</li>
              <li>Click "Generate ETL Mapping" to create column mappings</li>
              <li>Use "Load My Models" to select from your previously generated data models as target schema</li>
            </ul>
            <p className="mt-2 text-xs">
              <strong>JSON Format:</strong> {"{name: 'db_name', tables: [...], samples: {table_name: [...]}}"}
            </p>
            <p className="mt-2 text-xs text-textSecondary">
              Samples are optional but help improve mapping accuracy
            </p>
          </div>
          {/* Source DB Schema Input */}
          <div className="mb-2 max-w-lg mx-auto">
            <label className="block">
              <span className="font-semibold text-textPrimary">Source DB Schema (JSON or Excel):</span>
              <div className="w-full mt-1">
                <div className="flex w-full">
                  <label className="flex flex-1 cursor-pointer">
                    <span className="inline-flex items-center px-4 py-2 bg-actionPrimary text-white font-semibold rounded-l-lg border border-inputBorder border-r-0 text-sm" style={{ background: '#093878' }}>
                      Choose File
                    </span>
                    <span className={`flex-1 px-4 py-2 bg-panelHover text-textPrimary border border-inputBorder text-sm overflow-hidden whitespace-nowrap overflow-ellipsis ${sourceFileName !== 'No file chosen' ? 'border-r-0' : 'rounded-r-lg'}`}>
                      {sourceFileName}
                    </span>
                    <input
                      type="file"
                      accept=".json,application/json,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel"
                      className="hidden"
                      id="file_input_source"
                      onChange={async e => {
                        const file = e.target.files[0];
                        if (file) {
                          setSourceFileName(file.name);
                          const ext = file.name.split('.').pop().toLowerCase();
                          if (ext === 'json') {
                            handleFileUpload(file, true);
                          } else if (ext === 'xlsx' || ext === 'xls') {
                            // Excel file: send to backend for parsing
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const response = await api.uploadSourceSchemaFile(formData);
                              if (response && response.data) {
                                setSourceJson(JSON.stringify(response.data));
                                setSourceJsonError('');
                                setParsedSourceSchema(response.data);
                              } else {
                                setSourceJsonError('Failed to parse Excel file.');
                              }
                            } catch (err) {
                              setSourceJsonError('Failed to parse Excel file.');
                            }
                          } else {
                            setSourceJsonError('Unsupported file type. Please upload a JSON or Excel file.');
                          }
                        } else {
                          setSourceFileName('No file chosen');
                        }
                      }}
                    />
                  </label>
                  {sourceFileName !== 'No file chosen' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSourceFileName('No file chosen');
                        setSourceJson('');
                        setSourceJsonError('');
                        setParsedSourceSchema(null);
                        // Clear the file input
                        const fileInput = document.getElementById('file_input_source');
                        if (fileInput) {
                          fileInput.value = '';
                        }
                      }}
                      className="inline-flex items-center px-3 py-2 bg-semanticFail text-white font-semibold rounded-r-lg border border-semanticFail hover:bg-[#dc2626] transition-colors"
                      title="Delete file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {sourceJsonError && (
                <div className="etl-error-message">{sourceJsonError}</div>
              )}
            </label>
          </div>
          {/* Target DB Schema Input */}
          <div className="mb-2 max-w-lg mx-auto">
            <label className="block">
              <span className="font-semibold text-textPrimary">
              {/* Target DB Schema (disabled - data model selected) */}
                {selectedUserModel ? 'Target DB Schema (JSON or Excel)' : 'Target DB Schema (JSON or Excel):'}
              </span>
              <div className="w-full mt-1">
                <div className="flex w-full">
                  <label className={`flex flex-1 cursor-pointer ${selectedUserModel ? 'opacity-50 pointer-events-none' : ''}`}>
                    <span className={`inline-flex items-center px-4 py-2 font-semibold rounded-l-lg border border-inputBorder border-r-0 text-sm ${selectedUserModel ? 'bg-textSecondary/20 text-textSecondary' : 'bg-actionPrimary text-white'}`} style={{ background: '#093878' }}>
                      Choose File
                    </span>
                    <span className={`flex-1 px-4 py-2 bg-panelHover text-textPrimary border border-inputBorder text-sm overflow-hidden whitespace-nowrap overflow-ellipsis ${targetFileName !== 'No file chosen' ? 'border-r-0' : 'rounded-r-lg'}`}>
                      {targetFileName}
                    </span>
                    <input
                      type="file"
                      accept=".json,application/json,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel"
                      className="hidden"
                      id="file_input_target"
                      onChange={async e => {
                        const file = e.target.files[0];
                        if (file) {
                          setTargetFileName(file.name);
                                                  // Clear any selected user model when a file is uploaded
                        setSelectedUserModel(null);
                        setTargetFileName(file.name);
                          const ext = file.name.split('.').pop().toLowerCase();
                          if (ext === 'json') {
                            handleFileUpload(file, false);
                          } else if (ext === 'xlsx' || ext === 'xls') {
                            // Excel file: send to backend for parsing
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const response = await api.uploadSourceSchemaFile(formData);
                              if (response && response.data) {
                                setTargetJson(JSON.stringify(response.data));
                                setTargetJsonError('');
                                setParsedTargetSchema(response.data);
                              } else {
                                setTargetJsonError('Failed to parse Excel file.');
                              }
                            } catch (err) {
                              setTargetJsonError('Failed to parse Excel file.');
                            }
                          } else {
                            setTargetJsonError('Unsupported file type. Please upload a JSON or Excel file.');
                          }
                        } else {
                          setTargetFileName('No file chosen');
                        }
                      }}
                    />
                  </label>
                  {targetFileName !== 'No file chosen' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTargetFileName('No file chosen');
                        setTargetJson('');
                        setTargetJsonError('');
                        setParsedTargetSchema(null);
                        setSelectedUserModel(null);
                        // Clear the file input
                        const fileInput = document.getElementById('file_input_target');
                        if (fileInput) {
                          fileInput.value = '';
                        }
                      }}
                      className="inline-flex items-center px-3 py-2 bg-semanticFail text-white font-semibold rounded-r-lg border border-semanticFail hover:bg-[#dc2626] transition-colors"
                      title="Delete file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {targetJsonError && (
                <div className="etl-error-message">{targetJsonError}</div>
              )}
            </label>
          </div>

          {/* User Data Models Dropdown */}
          <UserDataModelsDropdown
            onModelSelect={handleUserModelSelection}
            onModelClear={clearSelectedUserModel}
            selectedModel={selectedUserModel}
            disabled={targetFileName !== 'No file chosen'}
            label={targetFileName !== 'No file chosen' ? 'Selected Data Model' : 'Or Select from Your Data Models:'}
          />
          {/* Generate Mapping button and messages */}
          <div className="flex gap-2 justify-center">
            <button
              onClick={handleETL}
              className="flex-1 max-w-xs bg-actionPrimary hover:bg-actionPrimaryHover text-white font-semibold py-2 px-4 rounded shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
              disabled={!!sourceJsonError || !!targetJsonError || isMappingLoading}
              style={{ background: '#093878' }}
            >
              {isMappingLoading ? 'Generating Mapping...' : 'Generate ETL Mapping'}
            </button>
          </div>
          {error && <div className="text-semanticFail mt-4 text-center">{error}</div>}
          {mappingError && <div className="text-semanticFail mt-4 text-center">{mappingError}</div>}
          {success && <div className="text-semanticPass mt-4 text-center">{success}</div>}
          {/* Schema Information Display */}
          {(parsedSourceSchema || parsedTargetSchema) && (
            <div className="p-3 bg-panelHover rounded-lg border border-borderLight mt-6">
              {/* ... existing schema info ... */}
              <h3 className="text-sm font-semibold text-textPrimary mb-2">📊 Schema Information</h3>
              <div className="grid grid-cols-2 gap-4 text-xs">
                {/* Source Schema Info */}
                <div className="bg-[rgba(8,145,178,0.08)] p-2 rounded border border-[rgba(8,145,178,0.3)]">
                  <h4 className="font-semibold text-accent mb-1">Source Schema</h4>
                  {parsedSourceSchema ? (
                    <>
                      <p className="text-accent"><strong>Database:</strong> {parsedSourceSchema.name}</p>
                      <p className="text-accent"><strong>Tables:</strong> {calculateSchemaStats(parsedSourceSchema).tableCount}</p>
                      <p className="text-accent"><strong>Columns:</strong> {calculateSchemaStats(parsedSourceSchema).columnCount}</p>
                      <div className="mt-1">
                        <p className="text-accent font-semibold">Table Names:</p>
                        <div className="max-h-16 overflow-y-auto">
                          {calculateSchemaStats(parsedSourceSchema).tableNames.map((tableName, index) => (
                            <span key={index} className="inline-block bg-[rgba(8,145,178,0.12)] text-accent px-1 py-0.5 rounded text-xs mr-1 mb-1">
                              {tableName}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-textSecondary">No source schema loaded</p>
                  )}
                </div>
                {/* Target Schema Info */}
                <div className="bg-[rgba(16,185,129,0.08)] p-2 rounded border border-[rgba(16,185,129,0.3)]">
                  <h4 className="font-semibold text-[#047857] mb-1">Target Schema</h4>
                  {parsedTargetSchema ? (
                    <>
                                             {selectedUserModel ? (
                        <p className="text-semanticPass mb-2">
                          <strong>Data Model:</strong> {selectedUserModel.display_name || selectedUserModel.full_response?.model_name || `Data Model ${selectedUserModel.id}`}
                          <br />
                          <span className="text-xs">Created: {new Date(selectedUserModel.timestamp).toLocaleString()}</span>
                        </p>
                      ) : (
                        <p className="text-semanticPass"><strong>Database:</strong> {parsedTargetSchema.name}</p>
                      )}
                      <p className="text-semanticPass"><strong>Tables:</strong> {calculateSchemaStats(parsedTargetSchema).tableCount}</p>
                      <p className="text-semanticPass"><strong>Columns:</strong> {calculateSchemaStats(parsedTargetSchema).columnCount}</p>
                      <div className="mt-1">
                        <p className="text-semanticPass font-semibold">Table Names:</p>
                        <div className="max-h-16 overflow-y-auto">
                          {calculateSchemaStats(parsedTargetSchema).tableNames.map((tableName, index) => (
                            <span key={index} className="inline-block bg-[rgba(16,185,129,0.15)] text-[#047857] px-1 py-0.5 rounded text-xs mr-1 mb-1">
                              {tableName}
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-textSecondary">No target schema loaded</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    ),
    // Tab 2: ETL Mapping Results
    (
      <div className="bg-white rounded-lg shadow-sm border border-borderLight p-6">
        <div className="max-w-6xl mx-auto">
          {/* Mapping Results Display */}
          {showMappingResults && mappingResults ? (
            <div className="p-4 bg-panelHover rounded-lg border border-borderLight mb-8">
              {/* ... existing mapping results ... */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold">Mapping Results</h3>
                <button
                  className="px-3 py-1 bg-semanticFail text-white rounded hover:bg-[#dc2626] transition"
                  onClick={() => {
                    setMappingResults(null);
                    setShowMappingResults(false);
                  }}
                  title="Clear all mapping results"
                >
                  Clear All
                </button>
              </div>
              {/* Mapping Statistics */}
              <div className="mb-4 p-3 bg-white rounded-lg border border-borderLight">
                <h4 className="text-sm font-semibold text-textPrimary mb-2">📈 Mapping Statistics</h4>
                <div className="grid grid-cols-4 gap-4 text-xs mb-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-accent">{calculateMappingStats(mappingResults).totalMappings}</div>
                    <div className="text-textSecondary">Total Mappings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">{calculateMappingStats(mappingResults).mappedColumns}</div>
                    <div className="text-textSecondary">Mapped Columns</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-semanticFail">{calculateMappingStats(mappingResults).unmappedColumns}</div>
                    <div className="text-textSecondary">Unmapped Columns</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-semanticPass">{calculateMappingStats(mappingResults).mappingSuccessRate}%</div>
                    <div className="text-textSecondary">Success Rate</div>
                  </div>
                </div>
              </div>
              {/* Mapping Table */}
              <div className="etl-mapping-table-container" style={{ overflowX: 'auto', maxHeight: '15rem' }}>
                <table className="etl-mapping-table w-full text-sm min-w-[800px]">
                  <thead className="bg-surfaceMuted">
                    <tr>
                      <th className="px-3 py-2 text-left">Source Table</th>
                      <th className="px-3 py-2 text-left">Source Column</th>
                      <th className="px-3 py-2 text-left">Target Table</th>
                      <th className="px-3 py-2 text-left">Target Column</th>
                      <th className="px-3 py-2 text-left">Transformation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappingResults.map((mapping, index) => {
                      const sourceTableName = mapping.source_table || extractTableName(mapping.source_column) || getTableNameFromSchema(mapping.source_column, parsedSourceSchema);
                      const targetTableName = mapping.target_table || extractTableName(mapping.target_column) || getTableNameFromSchema(mapping.target_column, parsedTargetSchema);
                      return (
                        <tr key={index} className="border-b border-borderLight">
                          <td className="px-3 py-2">
                            <span className="inline-block bg-[rgba(8,145,178,0.12)] text-accent px-2 py-1 rounded text-xs font-medium">
                              {sourceTableName || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">{mapping.source_column}</td>
                          <td className="px-3 py-2">
                            {targetTableName ? (
                              <span className="inline-block bg-[rgba(16,185,129,0.15)] text-[#047857] px-2 py-1 rounded text-xs font-medium">
                                {targetTableName}
                              </span>
                            ) : (
                              <span className="text-textSecondary text-xs">No mapping</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium">{mapping.target_column || 'No mapping'}</td>
                          <td className="px-3 py-2 text-xs text-textSecondary">{mapping.transformation || 'None'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Generate Transformation button */}
              <div className="mt-6 p-4 bg-white rounded-lg border border-borderLight">
                {/* <h4 className="text-lg font-semibold mb-4 text-textPrimary">🔄 Generate Transformation Scripts</h4> */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-4">
                  <button
                    onClick={() => setActiveTab(2)}
                    disabled={!mappingResults || mappingResults.length === 0}
                    className="px-6 py-2 text-white font-semibold rounded shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                    style={{ background: 'linear-gradient(90deg, rgb(47, 221, 105) 0%, rgba(104, 238, 167, 0.9) 100%)' }}
                  >
                    Generate Transformation
                  </button>
                </div>
                {scriptGenerationError && (
                  <div className="text-semanticFail text-center mb-4 p-3 bg-[rgba(239,68,68,0.08)] rounded-lg border border-[rgba(239,68,68,0.3)]">
                    {scriptGenerationError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-textSecondary text-center p-8">No mapping results to display. Please generate ETL mapping first.</div>
          )}
        </div>
      </div>
    ),
    // Tab 3: Script Generator
    (
      <div className="bg-white rounded-lg shadow-sm border border-borderLight p-6">
        <div className="max-w-6xl mx-auto">
          {!mappingResults || !parsedSourceSchema || !parsedTargetSchema ? (
            <div className="text-center p-8">
              <h3 className="text-lg font-semibold text-textPrimary mb-4">❌ Missing Required Data</h3>
              <p className="text-textSecondary mb-4">Please go back to the first tab and generate mapping results first.</p>
              <button 
                onClick={() => setActiveTab(0)}
                className="px-4 py-2 bg-actionPrimary hover:bg-actionPrimaryHover text-white rounded transition"
              >
                ← Back to Schema Upload
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <ScriptGeneratorHeader
                onBack={() => setActiveTab(1)}
                mappingCount={mappingResults ? mappingResults.length : 0}
                onToggleHistory={() => setShowHistorySidebar(!showHistorySidebar)}
                showHistory={showHistorySidebar}
                scriptHistoryCount={scriptHistory.length}
              />
              <ScriptGeneratorControlPanel
                selectedTechnology={selectedTechnology}
                setSelectedTechnology={setSelectedTechnology}
                isGenerating={isGenerating}
                generateScripts={generateScriptsWithBatching}
                currentBatch={currentBatch}
                totalBatches={totalBatches}
              />
              {finalScript && (
                <>
                  {console.log('finalScript:', finalScript)}
                  <ScriptEditor
                    finalScript={finalScript}
                    setFinalScript={setFinalScript}
                    scriptLanguage={scriptLanguage}
                    editorTheme={editorTheme}
                    generatedScripts={generatedScripts}
                    currentScriptId={currentScriptId}
                    selectedTechnology={selectedTechnology}
                  />
                </>
              )}
              <ScriptActions
                finalScript={finalScript}
                copyToClipboard={copyToClipboard}
                downloadScript={downloadScript}
              />
              <BatchInfo generatedScripts={generatedScripts} />
            </div>
          )}
          
          {/* Script History Sidebar */}
          {showHistorySidebar && (
            <ScriptHistorySidebar
              scriptHistory={scriptHistory}
              currentScriptId={currentScriptId}
              onLoadScript={loadScriptFromHistory}
              onDeleteScript={deleteScriptFromHistory}
              onClose={() => setShowHistorySidebar(false)}
            />
          )}
        </div>
      </div>
    ),
  ];

  // If visualizer is active, show the schema visualization
  if (showVisualizer && parsedSourceSchema && parsedTargetSchema) {
    return (
      <div className="etl-visualizer-container">
        <div className="etl-visualizer-header">
          <button
            onClick={toggleVisualizer}
            className="etl-back-button"
          >
            ← Back to ETL Tool
          </button>
          <h2 className="etl-visualizer-title">Database Schema Visualization</h2>
          <div className="etl-file-info">
            <span>Source: {sourceFileName}</span>
            <span>Target: {targetFileName}</span>
          </div>
        </div>
        <SchemaVisualizer 
          sourceSchema={parsedSourceSchema} 
          targetSchema={parsedTargetSchema} 
          mappingResults={mappingResults}
        />
      </div>
    );
  }

  // Main tabbed layout
  return (
    <div className="flex justify-center bg-surfaceMuted pt-6 min-h-[100vh]">
      <div className="w-full max-w-5xl mx-auto">
        <div className="tabs">
          <div className="flex">
            <ul className="flex border border-borderLight rounded-xl transition-all duration-300 -mb-px overflow-hidden">
              {tabHeaders.map((header, idx) => (
                <li
                  key={header}
                  className={`px-6 py-2 cursor-pointer font-semibold text-sm transition-all duration-300 ${
                    activeTab === idx
                      ? 'bg-white border-b-2 border-accent text-accent shadow-sm'
                      : 'bg-panelHover text-textSecondary hover:text-accent hover:bg-white'
                  }`}
                  onClick={() => setActiveTab(idx)}
                >
                  {header}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3">
            {tabBodies[activeTab]}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ETL_Tool;